import { useApolloClient } from '@apollo/client';
import { type Board, type User } from '@apps/www/src/__generated__/graphql';
import useAuthTeam from '@apps/www/src/www/hooks/useAuthTeam';
import useAuthUserID from '@apps/www/src/www/hooks/useAuthUserID';
import BoardItemsQuery from '@apps/www/src/www/queries/BoardItemsQuery';
import RemoveUserFromBoardMutation from '@apps/www/src/www/queries/RemoveUserFromBoardMutation';
import RemoveUserFromTeamMutation from '@apps/www/src/www/queries/RemoveUserFromTeamMutation';
import { SVButtonSIZES } from '@pkgs/shared-client/components/SVButton';
import SVForm from '@pkgs/shared-client/components/SVForm';
import SVModal, { useModalClose } from '@pkgs/shared-client/components/SVModal';
import fieldNameFromQuery from '@pkgs/shared-client/helpers/fieldNameFromQuery';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import InviteType from '@pkgs/shared/enums/InviteType';

const _SVRemoveUserFromTeamOrBoardModal = (
	props: {
		userID: User['_id'] | undefined;
		name: User['name'] | undefined;
		email: string | undefined;
	} & (
		| {
				type: typeof InviteType.TEAM;
		  }
		| {
				type: typeof InviteType.BOARD;
				_id: Board['_id'];
		  }
	),
) => {
	const { userID, name, email } = props;
	const close = useModalClose();
	const client = useApolloClient();
	const authUserID = useAuthUserID();

	const team = useAuthTeam();
	const teamName = team?.name;
	const isSelf = userID === authUserID;

	const handleSubmit = useEventCallback(async () => {
		if (props.type === InviteType.TEAM) {
			await client.mutate({
				mutation: RemoveUserFromTeamMutation,
				variables: {
					input: {
						userID: userID,
						email: email,
					},
				},
			});
		} else if (props.type === InviteType.BOARD) {
			await client.mutate({
				mutation: RemoveUserFromBoardMutation,
				variables: {
					input: {
						boardID: props._id,
						userID: userID,
						email: email,
					},
				},
			});
			// Remove query from cache to refetch the updated items
			const fieldName = fieldNameFromQuery(BoardItemsQuery, {
				boardID: props._id,
			});
			if (fieldName) {
				client.cache.evict({
					id: 'ROOT_QUERY',
					fieldName,
					broadcast: false,
				});
			}

			client.cache.gc();
		}

		if (isSelf) {
			await close();
			window.location.href = '/';
			return;
		}

		await close();
	});

	return (
		<>
			{!isSelf ? (
				<SVModal.Title>
					Remove {name || email} from {props.type === InviteType.TEAM ? 'team' : 'board'}?
				</SVModal.Title>
			) : (
				<div>
					<h3 className="type-small-title block max-w-[300px] text-left font-medium">
						You are about to leave the{' '}
						{props.type === InviteType.TEAM ? `team ${teamName || ''}` : 'board'}
					</h3>
				</div>
			)}
			<SVForm
				onSubmit={handleSubmit}
				submitLabel={isSelf ? 'Yes, do it' : 'Remove member'}
				isDelete={true}
				noPadding={true}
				submitAlignment={isSelf ? 'left' : 'right'}
				submitClassName="type-label type-label rounded-[16px] bg-gray-200 px-6 py-5 mt-5"
				submitSize={SVButtonSIZES.TINY}
			>
				{isSelf ? (
					<>
						<div className="mb-6 mt-2">
							{props.type === InviteType.TEAM
								? 'You will be removed and lose access from all team boards.'
								: 'You will lose access to this board.'}
						</div>
						<div className="mb-10 mt-2">This action cannot be undone.</div>
					</>
				) : null}
			</SVForm>
		</>
	);
};

const SVRemoveUserFromTeamOrBoardModal = SVModal.create(_SVRemoveUserFromTeamOrBoardModal, {
	className: 'w-[340px] -sm:w-full',
});

export default SVRemoveUserFromTeamOrBoardModal;
